import { useState, useEffect } from 'react';
import { Sidebar } from '../layout/Sidebar';
import { TopBar } from '../layout/TopBar';
import { CustomersTable, TableCustomer } from './CustomersTable';
import { AddCustomerModal } from './AddCustomerModal';
import { EditCustomerModal } from './EditCustomerModal';
import { DeleteCustomerModal } from './DeleteCustomerModal';
import { CustomerTypesModal } from './CustomerTypesModal';
import { Plus, Grid3x3, List, SlidersHorizontal, X, Users, MoreHorizontal, Mail, Phone, MapPin, Wallet, ShoppingBag, RefreshCw, Settings2, Search } from 'lucide-react';
import { useCustomers } from '../../hooks/useCustomers';
import { useOrders } from '../../hooks/useOrders';
import { usePageTitle } from '../../hooks/usePageTitle';
import { useCurrency } from '../../hooks/useCurrency';
import { customerTypesService, CustomerType } from '../../services/customerTypes.service';
import { customersService } from '../../services/customers.service';
import { toast } from 'sonner';

interface CustomersPageProps {
  onNavigate?: (page: string) => void;
  onAddCustomer?: () => void;
  customerType?: 'all' | 'general' | 'members';
}

const TIER_COLORS: Record<string, string> = {
  BRONZE:   'bg-orange-50 dark:bg-orange-900/20 text-orange-700 dark:text-orange-400',
  SILVER:   'bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300',
  GOLD:     'bg-yellow-50 dark:bg-yellow-900/20 text-yellow-700 dark:text-yellow-400',
  PLATINUM: 'bg-indigo-50 dark:bg-indigo-900/20 text-indigo-700 dark:text-indigo-400',
};

const TIER_ICONS: Record<string, string> = {
  BRONZE: '🥉', SILVER: '🥈', GOLD: '🥇', PLATINUM: '💎',
};

export function CustomersPage({ onNavigate, /*onAddCustomer,*/ customerType = 'all' }: CustomersPageProps) {
  // Update document title dynamically
  usePageTitle('Customers');

  const [sidebarOpen, setSidebarOpen] = useState(() =>
    typeof window !== 'undefined' ? window.innerWidth >= 768 : true
  );
  const [viewMode, setViewMode] = useState<'grid' | 'table'>('table');
  const [searchQuery, setSearchQuery] = useState('');
  const [showFilters, setShowFilters] = useState(false);
  const [statusFilter, _setStatusFilter] = useState<'all' | 'active' | 'inactive'>('all');
  const [_selectedCustomer, _setSelectedCustomer] = useState<TableCustomer | null>(null);
  const [_showEditModal, _setShowEditModal] = useState(false);
  const [_showDeleteModal, _setShowDeleteModal] = useState(false);
  const [showAddModal, setShowAddModal] = useState(false);
  const [selectedFilterField, setSelectedFilterField] = useState('');
  const [filterValue, setFilterValue] = useState('');
  const [editingCustomerId, setEditingCustomerId] = useState<string | null>(null);
  const [deletingCustomer, setDeletingCustomer] = useState<{ id: string; name: string } | null>(null);
  const [openCardDropdown, setOpenCardDropdown] = useState<string | null>(null);
  const [customerTypeFilter, setCustomerTypeFilter] = useState<string>('all');
  const [availableTypes, setAvailableTypes] = useState<CustomerType[]>([]);
  const [showTypesModal, setShowTypesModal] = useState(false);

  useEffect(() => {
    customerTypesService.getAll().then((r) => { if (r.success && r.data) setAvailableTypes(r.data); });
  }, []);

  const isSubscriptionBased = localStorage.getItem('isSubscriptionBased') === 'true';

  const {
    customers: apiCustomers,
    loading: customersLoading,
    refresh: refreshCustomers,
    deleteCustomer,
    total,
    limit,
    page,
    totalPages,
    updateQuery,
  } = useCustomers();
  const { orders: ordersData } = useOrders({ page: 1, limit: 50 });
  const { formatCurrency } = useCurrency();

  const formatSpentAmount = (amount: number | string) => {
    const numeric = typeof amount === 'number' ? amount : parseFloat(amount as string);
    return formatCurrency(Number.isFinite(numeric) ? numeric : 0);
  };

  // Debug logging for orders count
  useEffect(() => {
    console.log('📊 [CustomersPage] Orders Data:', ordersData);
    console.log('📊 [CustomersPage] Orders Count:', ordersData?.length || 0);
  }, [ordersData]);

  // Map API customers to table format
  const allCustomers: TableCustomer[] = apiCustomers.map((customer) => ({
    id: customer.id,
    joinDate: new Date(customer.createdAt).toLocaleDateString(),
    name: `${customer.firstName} ${customer.lastName}`,
    email: customer.email,
    phone: customer.phone || 'N/A',
    location:
      customer.city && customer.state
        ? `${customer.city}, ${customer.state}`
        : customer.city || customer.address || 'N/A',
    totalSpent: parseFloat(customer.totalSpent as any) || 0,
    totalOrders: customer._count?.orders ?? customer.totalOrders ?? 0,
    lastOrder: customer.updatedAt
      ? new Date(customer.updatedAt).toLocaleDateString()
      : '—',
    membershipTier: customer.membershipTier || 'BRONZE',
    isMember: ['SILVER', 'GOLD', 'PLATINUM'].includes(customer.membershipTier),
    isActive: customer.isActive,
    customerType: customer.customerType?.name || '',
    customerNumber: customer.customerNumber || null,
    subscriptionFrequency: customer.subscriptionFrequency || null,
    subscriptionEndDate: customer.subscriptionEndDate || null,
  }));

  // Apply customerType filter
  const typeFiltered =
    customerTypeFilter !== 'all'
      ? allCustomers.filter((c) => c.customerType === customerTypeFilter)
      : customerType === 'general'
      ? allCustomers.filter((c) => !c.isMember)
      : customerType === 'members'
      ? allCustomers.filter((c) => c.isMember)
      : allCustomers;

  // Apply status filter
  const statusFiltered =
    statusFilter === 'active'
      ? typeFiltered.filter((c) => c.isActive)
      : statusFilter === 'inactive'
      ? typeFiltered.filter((c) => !c.isActive)
      : typeFiltered;

  // Apply search filter
  const searchFiltered = statusFiltered.filter((c) => {
    const sv = searchQuery.toLowerCase();
    return (
      c.id.toLowerCase().includes(sv) ||
      c.name.toLowerCase().includes(sv) ||
      c.location.toLowerCase().includes(sv) ||
      c.totalSpent.toString().includes(sv) ||
      c.lastOrder.toLowerCase().includes(sv)
    );
  });

  const filterFields = [
    { value: 'id', label: 'Customer ID' },
    { value: 'name', label: 'Customer Name' },
    { value: 'location', label: 'Location' },
    { value: 'totalSpent', label: 'Total Spent' },
    { value: 'lastOrder', label: 'Last Order' },
    { value: 'customerType', label: 'Customer Type' },
  ];

  const pageTitle =
    customerType === 'general'
      ? 'General Customers'
      : customerType === 'members'
      ? 'Members'
      : 'All Customers';

  const pageSubtitle =
    customerType === 'general'
      ? 'Bronze tier customers'
      : customerType === 'members'
      ? 'Silver, Gold and Platinum tier customers'
      : 'Complete customers list including all members';

  const handleFilterFieldSelect = (field: string) => {
    setSelectedFilterField(field);
    setFilterValue('');
  };

  const handleClearFilter = () => {
    setSelectedFilterField('');
    setFilterValue('');
    setShowFilters(false);
  };

  const handleViewDetails = (customerId: string) => {
    onNavigate?.(`customer-details-${customerId}`);
  };

  const handleEditCustomer = (_customerId: string, _data: any) => {
    refreshCustomers();
  };

  const handleDeleteCustomer = async (customerId: string) => {
    try {
      await deleteCustomer(customerId);
      setDeletingCustomer(null);
      toast.success('Customer deleted successfully');
    } catch (err: any) {
      toast.error(err.message || 'Failed to delete customer');
    }
  };

  const handleToggleActive = async (customerId: string, currentIsActive: boolean) => {
    try {
      await customersService.updateCustomer(customerId, { isActive: !currentIsActive });
      refreshCustomers();
      toast.success(currentIsActive ? 'Customer deactivated' : 'Customer activated');
    } catch (err: any) {
      toast.error(err.message || 'Failed to update customer status');
    }
  };

  const handleAddCustomer = (_data: any) => {
    refreshCustomers();
    setShowAddModal(false);
  };

  const activePageKey =
    customerType === 'general'
      ? 'customers-general'
      : customerType === 'members'
      ? 'customers-members'
      : 'customers';

  // Apply filterValue to the selected field
  const gridFiltered = selectedFilterField && filterValue
    ? searchFiltered.filter((c) => {
        const fieldValue = c[selectedFilterField as keyof TableCustomer];
        return String(fieldValue).toLowerCase().includes(filterValue.toLowerCase());
      })
    : searchFiltered;

  return (
    <div className="flex h-screen bg-gray-50 dark:bg-gray-950">
      <Sidebar
        isOpen={sidebarOpen}
        onClose={() => setSidebarOpen(!sidebarOpen)}
        activePage={activePageKey}
        onNavigate={onNavigate}
        ordersCount={ordersData?.length || 0}
      />

      <div className={`flex-1 flex flex-col overflow-hidden transition-all duration-300 ${
        sidebarOpen ? 'min-[768px]:ml-64' : 'ml-0'
      }`}>
        <TopBar onMenuClick={() => setSidebarOpen(!sidebarOpen)} sidebarOpen={sidebarOpen} onNavigate={onNavigate} />

        <main className="flex-1 overflow-y-auto p-6">
          <div className="max-w-[1400px] mx-auto">

            {/* Page Header */}
            <div className="mb-6 flex items-start justify-between gap-4">
              <div>
                <h1 className="text-2xl font-bold text-gray-900 dark:text-white">{pageTitle}</h1>
                <p className="text-gray-400 dark:text-gray-500 mt-1 text-sm">{pageSubtitle}</p>
              </div>

              <div className="flex items-center gap-3 flex-shrink-0">

                {/* Search */}
                <div className="relative flex-1 lg:max-w-xs">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder="Search Customers..."
                    className="w-full pl-10 pr-4 py-2 text-sm bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 border border-gray-200 dark:border-gray-700 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>
                
                {/* Customer Type Quick Filter */}
                {/* {availableTypes.length > 0 && (
                  <div className="flex items-center gap-2">
                    {(['all', ...availableTypes.map((t) => t.name)]).map((type) => (
                      <button
                        key={type}
                        onClick={() => setCustomerTypeFilter(type)}
                        className={`px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border ${
                          customerTypeFilter === type
                            ? 'bg-indigo-600 text-white border-indigo-600'
                            : 'bg-white dark:bg-gray-800 text-gray-600 dark:text-gray-300 border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700'
                        }`}
                      >
                        {type === 'all' ? 'All Types' : type}
                      </button>
                    ))}
                  </div>
                )} */}

                {availableTypes.length > 0 && (
                  <div className="flex items-center gap-2">
                    <select
                      value={customerTypeFilter}
                      onChange={(e) => setCustomerTypeFilter(e.target.value)}
                      className="px-3 py-2 rounded-lg text-sm border bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 border-gray-200 dark:border-gray-700 focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    >
                      <option value="all">All Customers</option>
                      {availableTypes.map((t) => (
                        <option key={t.name} value={t.name}>
                          {t.name}
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Manage Types Button */}
                {/* <button
                  onClick={() => setShowTypesModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                >
                  <Settings2 className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" />
                  <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">Types</span>
                </button> */}

                {/* Grid / List Toggle */}
                <div className="flex items-center gap-1 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg p-1">
                  <button
                    onClick={() => setViewMode('grid')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      viewMode === 'grid'
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <Grid3x3 className="w-3.5 h-3.5" /> Grid
                  </button>
                  <button
                    onClick={() => setViewMode('table')}
                    className={`flex items-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                      viewMode === 'table'
                        ? 'bg-indigo-600 text-white'
                        : 'text-gray-600 dark:text-gray-400 hover:bg-gray-100 dark:hover:bg-gray-700'
                    }`}
                  >
                    <List className="w-3.5 h-3.5" /> List
                  </button>
                </div>

                {/* Add Customer Button */}
                <button
                  onClick={() => setShowAddModal(true)}
                  className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-indigo-600 text-white rounded-lg hover:bg-indigo-700 transition-colors"
                >
                  <Plus className="w-3.5 h-3.5 sm:w-4 sm:h-4" />
                  <span className="text-xs sm:text-sm font-medium">Add Customer</span>
                </button>

                {/* Filter Button */}
                <div className="relative">
                  <button
                    onClick={() => setShowFilters(!showFilters)}
                    className="flex items-center gap-2 px-3 py-1.5 sm:px-4 sm:py-2 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                  >
                    <SlidersHorizontal className="w-3.5 h-3.5 sm:w-4 sm:h-4 text-gray-600 dark:text-gray-400" />
                    <span className="text-xs sm:text-sm font-medium text-gray-900 dark:text-white">Filter</span>
                  </button>

                  {showFilters && (
                    <>
                      <div className="fixed inset-0 z-10" onClick={() => setShowFilters(false)} />
                      <div className="absolute right-0 mt-2 w-80 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-lg z-20 p-4">
                        <div className="flex items-center justify-between mb-4">
                          <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Filter Customers</h3>
                          <button
                            onClick={() => setShowFilters(false)}
                            className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                          >
                            <X className="w-4 h-4 text-gray-500 dark:text-gray-400" />
                          </button>
                        </div>

                        <div className="mb-4">
                          <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">Filter By Field</label>
                          <div className="space-y-2">
                            {filterFields.map((field) => (
                              <button
                                key={field.value}
                                onClick={() => handleFilterFieldSelect(field.value)}
                                className={`w-full text-left px-3 py-2 rounded-lg text-xs font-medium transition-colors ${
                                  selectedFilterField === field.value
                                    ? 'bg-indigo-50 dark:bg-indigo-900/30 text-indigo-600 dark:text-indigo-400 border border-indigo-200 dark:border-indigo-700'
                                    : 'bg-gray-50 dark:bg-gray-700 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-600 border border-transparent'
                                }`}
                              >
                                {field.label}
                              </button>
                            ))}
                          </div>
                        </div>

                        {selectedFilterField && (
                          <div className="mb-4">
                            <label className="block text-xs font-semibold text-gray-700 dark:text-gray-300 mb-2">
                              Search {filterFields.find((f) => f.value === selectedFilterField)?.label}
                            </label>
                            <input
                              type="text"
                              value={filterValue}
                              onChange={(e) => setFilterValue(e.target.value)}
                              placeholder="Type to filter..."
                              className="w-full px-3 py-2 text-xs border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-500 rounded-lg focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500"
                            />
                          </div>
                        )}

                        <div className="flex items-center gap-2">
                          <button
                            onClick={handleClearFilter}
                            className="flex-1 px-4 py-2 text-xs font-medium text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                          >
                            Clear
                          </button>
                          <button
                            onClick={() => setShowFilters(false)}
                            className="flex-1 px-4 py-2 text-xs font-medium text-white bg-indigo-600 rounded-lg hover:bg-indigo-700 transition-colors"
                          >
                            Apply
                          </button>
                        </div>
                      </div>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Active Filter Badge */}
            {selectedFilterField && filterValue && (
              <div className="mb-6 flex items-center gap-2">
                <div className="inline-flex items-center gap-2 px-3 py-1.5 bg-indigo-50 dark:bg-indigo-900/30 text-indigo-700 dark:text-indigo-400 rounded-lg text-xs font-medium">
                  <span>
                    {filterFields.find((f) => f.value === selectedFilterField)?.label}: "{filterValue}"
                  </span>
                  <button
                    onClick={handleClearFilter}
                    className="hover:bg-indigo-100 dark:hover:bg-indigo-800 rounded-full p-0.5 transition-colors"
                  >
                    <X className="w-3 h-3" />
                  </button>
                </div>
              </div>
            )}

            {/* ── LIST VIEW ── */}
            {viewMode === 'table' && (
              <CustomersTable
                customers={gridFiltered}
                page={page}
                totalPages={totalPages}
                totalCount={total}
                limit={limit}
                onPageChange={updateQuery}
                loading={customersLoading}
                filterField={selectedFilterField}
                filterValue={filterValue}
                customerType={customerType}
                onToggleActive={handleToggleActive}
                onViewDetails={handleViewDetails}
              />
            )}

            {/* ── GRID VIEW ── */}
            {viewMode === 'grid' && (
              <>
                {customersLoading ? (
                  <div className="flex items-center justify-center py-20">
                    <div className="text-center">
                      <div className="w-10 h-10 border-4 border-indigo-600 border-t-transparent rounded-full animate-spin mx-auto mb-3" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">Loading customers…</p>
                    </div>
                  </div>
                ) : gridFiltered.length === 0 ? (
                  <div className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-12 text-center">
                    <div className="w-16 h-16 bg-gray-100 dark:bg-gray-800 rounded-full flex items-center justify-center mx-auto mb-4">
                      <Users className="w-8 h-8 text-gray-400 dark:text-gray-500" />
                    </div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white mb-2">No Customers Found</h3>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      {selectedFilterField && filterValue
                        ? 'No customers match your current filter.'
                        : 'No customers in this category yet.'}
                    </p>
                  </div>
                ) : (
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
                    {gridFiltered.map((customer) => {
                      const initials = customer.name
                        .split(' ')
                        .map((n) => n[0])
                        .join('')
                        .slice(0, 2)
                        .toUpperCase();
                      const tierColor = TIER_COLORS[customer.membershipTier] || TIER_COLORS.BRONZE;
                      const tierIcon = TIER_ICONS[customer.membershipTier] || '🥉';

                      return (
                        <div
                          key={customer.id}
                          onClick={() => handleViewDetails(customer.id)}
                          className="bg-white dark:bg-gray-900 rounded-2xl shadow-sm border border-gray-100 dark:border-gray-800 p-6 hover:shadow-md hover:border-indigo-200 dark:hover:border-indigo-700 transition-all cursor-pointer"
                        >
                          {/* Card Header */}
                          <div className="flex items-start justify-between mb-4">
                            <div className="flex items-center gap-3">
                              <div className="w-12 h-12 bg-gradient-to-br from-indigo-400 to-purple-500 rounded-full flex items-center justify-center flex-shrink-0">
                                <span className="text-base font-bold text-white">{initials}</span>
                              </div>
                              <div>
                                <h3 className="text-sm font-semibold text-gray-900 dark:text-white leading-tight">
                                  {customer.name}
                                </h3>
                              <div className="flex items-center gap-1 flex-wrap mt-1">
                                <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${tierColor}`}>
                                  {tierIcon} {customer.membershipTier}
                                </span>
                                {customer.customerType && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-purple-50 dark:bg-purple-900/20 text-purple-700 dark:text-purple-400">
                                    {customer.customerType}
                                  </span>
                                )}
                                {customer.customerNumber && (
                                  <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-mono font-semibold bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300">
                                    #{customer.customerNumber}
                                  </span>
                                )}
                              </div>
                              </div>
                            </div>

                            {/* Actions Dropdown */}
                            <div className="relative" onClick={(e) => e.stopPropagation()}>
                              <button
                                onClick={() => setOpenCardDropdown(openCardDropdown === customer.id ? null : customer.id)}
                                className="p-1.5 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-lg transition-colors"
                              >
                                <MoreHorizontal className="w-4 h-4 text-gray-400 dark:text-gray-500" />
                              </button>
                              {openCardDropdown === customer.id && (
                                <>
                                  <div className="fixed inset-0 z-10" onClick={() => setOpenCardDropdown(null)} />
                                  <div className="absolute right-0 mt-1 w-40 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-xl shadow-lg z-20 py-2">
                                    <button
                                      className="w-full text-left px-4 py-2 text-xs font-medium text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                      onClick={() => { setOpenCardDropdown(null); handleViewDetails(customer.id); }}
                                    >
                                      View Detail
                                    </button>
                                    <button
                                      className="w-full text-left px-4 py-2 text-xs font-medium text-gray-900 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                      onClick={() => { setOpenCardDropdown(null); setEditingCustomerId(customer.id); }}
                                    >
                                      Edit
                                    </button>
                                    <button
                                      className="w-full text-left px-4 py-2 text-xs font-medium text-red-600 dark:text-red-400 hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors"
                                      onClick={() => { setOpenCardDropdown(null); setDeletingCustomer({ id: customer.id, name: customer.name }); }}
                                    >
                                      Delete
                                    </button>
                                  </div>
                                </>
                              )}
                            </div>
                          </div>

                          {/* Contact Info */}
                          <div className="space-y-2 mb-4">
                            <div className="flex items-center gap-2">
                              <Mail className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                              <span className="text-xs text-gray-600 dark:text-gray-400 truncate">{customer.email}</span>
                            </div>
                            {customer.phone && customer.phone !== 'N/A' && (
                              <div className="flex items-center gap-2">
                                <Phone className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span className="text-xs text-gray-600 dark:text-gray-400">{customer.phone}</span>
                              </div>
                            )}
                            {customer.location && customer.location !== 'N/A' && (
                              <div className="flex items-center gap-2">
                                <MapPin className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500 flex-shrink-0" />
                                <span className="text-xs text-gray-600 dark:text-gray-400">{customer.location}</span>
                              </div>
                            )}
                          </div>

                          {/* Stats Row */}
                          <div className="flex items-center justify-between pt-4 border-t border-gray-100 dark:border-gray-800">
                            <div className="flex items-center gap-1.5">
                              <Wallet className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                              <span className="text-xs font-semibold text-gray-900 dark:text-white">
                                {formatSpentAmount(customer.totalSpent)}
                              </span>
                            </div>
                            <div className="flex items-center gap-1.5">
                              <ShoppingBag className="w-3.5 h-3.5 text-gray-400 dark:text-gray-500" />
                              <span className="text-xs text-gray-600 dark:text-gray-400">
                                {customer.totalOrders} orders
                              </span>
                            </div>
                            <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              customer.isActive
                                ? 'bg-green-50 dark:bg-green-900/30 text-green-700 dark:text-green-400'
                                : 'bg-gray-100 dark:bg-gray-800 text-gray-500 dark:text-gray-400'
                            }`}>
                              {customer.isActive ? 'Active' : 'Inactive'}
                            </span>
                          </div>

                          {/* Join Date */}
                          <div className="mt-3 text-xs text-gray-400 dark:text-gray-500">
                            Joined {customer.joinDate}
                          </div>

                          {/* Subscription Info */}
                          {isSubscriptionBased && customer.subscriptionFrequency && (
                            <div className="mt-2 flex items-center gap-1.5">
                              <RefreshCw className="w-3 h-3 text-indigo-400" />
                              <span className="text-xs text-indigo-600 dark:text-indigo-400 font-medium">
                                {customer.subscriptionFrequency} subscription
                              </span>
                              {customer.subscriptionEndDate && new Date(customer.subscriptionEndDate) < new Date() && (
                                <span className="text-xs text-red-500 font-medium">· Expired</span>
                              )}
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                )}
              </>
            )}
          </div>
        </main>
      </div>

      {editingCustomerId && (
        <EditCustomerModal
          customerId={editingCustomerId}
          onClose={() => setEditingCustomerId(null)}
          onSave={handleEditCustomer}
        />
      )}

      {deletingCustomer && (
        <DeleteCustomerModal
          customer={deletingCustomer}
          onClose={() => setDeletingCustomer(null)}
          onDelete={handleDeleteCustomer}
        />
      )}

      {showAddModal && (
        <AddCustomerModal
          onClose={() => setShowAddModal(false)}
          onSave={handleAddCustomer}
        />
      )}

      {showTypesModal && (
        <CustomerTypesModal
          onClose={() => {
            setShowTypesModal(false);
            customerTypesService.getAll().then((r) => { if (r.success && r.data) setAvailableTypes(r.data); });
          }}
        />
      )}
    </div>
  );
}